import { apiClient, type Paginated } from '@/core/api';
import type {
  AnnouncementDto,
  AnnouncementListParams,
  AnnouncementWriteInput,
  MessageDto,
  MessageReportCreateInput,
  MessageReportDto,
  MessageReportListParams,
  MessageReportReviewInput,
  ThreadDto,
  ThreadListParams,
} from './types';

/** `GET /api/threads/` — the caller's conversations, newest activity first. */
export async function getThreads(params: ThreadListParams = {}): Promise<Paginated<ThreadDto>> {
  const { data } = await apiClient.get<Paginated<ThreadDto>>('/threads/', { params });
  return data;
}

/** `GET /api/threads/{public_id}/` */
export async function getThread(publicId: string): Promise<ThreadDto> {
  const { data } = await apiClient.get<ThreadDto>(`/threads/${publicId}/`);
  return data;
}

/**
 * `GET /api/threads/{public_id}/messages/` — the history API.
 *
 * `since` takes an ISO-8601 timestamp or the public_id of the last message the
 * client rendered (backend ADR-020), which is what the poller sends. This stays
 * the fallback path whether or not the socket is up.
 *
 * Messages are ordered OLDEST-first server-side (`Message.Meta.ordering`), so
 * page 1 is the start of the conversation and the newest page is the last one.
 * `page: 'last'` is DRF's own `last_page_strings` — the only way to ask for the
 * newest page without first fetching a page to learn `count`.
 */
export async function getMessages(
  publicId: string,
  params: { since?: string; page?: number | 'last'; page_size?: number } = {}
): Promise<Paginated<MessageDto>> {
  const { data } = await apiClient.get<Paginated<MessageDto>>(`/threads/${publicId}/messages/`, {
    params,
  });
  return data;
}

/** `POST /api/threads/{public_id}/messages/` */
export async function postMessage(publicId: string, body: string): Promise<MessageDto> {
  const { data } = await apiClient.post<MessageDto>(`/threads/${publicId}/messages/`, { body });
  return data;
}

/** `POST /api/threads/{public_id}/read/` — advance the caller's read cursor. */
export async function markThreadRead(publicId: string): Promise<ThreadDto> {
  const { data } = await apiClient.post<ThreadDto>(`/threads/${publicId}/read/`);
  return data;
}

/**
 * `POST /api/threads/direct/ {member}` — get-or-create the DM with another
 * member (201 created, 200 existing).
 *
 * The DM matrix (backend ADR-020) is enforced here, server-side, and its
 * refusals are rendered verbatim: the client never re-implements the rules.
 */
export async function createDirectThread(memberPublicId: string): Promise<ThreadDto> {
  const { data } = await apiClient.post<ThreadDto>('/threads/direct/', {
    member: memberPublicId,
  });
  return data;
}

/** `PATCH /api/threads/{public_id}/` — the caller's OWN mute flag, nothing else. */
export async function setThreadMuted(publicId: string, isMuted: boolean): Promise<ThreadDto> {
  const { data } = await apiClient.patch<ThreadDto>(`/threads/${publicId}/`, {
    is_muted: isMuted,
  });
  return data;
}

/** `DELETE /api/messages/{public_id}/` — soft delete by the author or by staff. */
export async function deleteMessage(publicId: string): Promise<void> {
  await apiClient.delete(`/messages/${publicId}/`);
}

// ---------------------------------------------------------------------------
// Announcements
// ---------------------------------------------------------------------------

/** `GET /api/announcements/` — role-shaped readable set (staff all; pupils
 *  school-wide + enrolled classes; parents school-wide + their children's). */
export async function getAnnouncements(
  params: AnnouncementListParams = {}
): Promise<Paginated<AnnouncementDto>> {
  const { data } = await apiClient.get<Paginated<AnnouncementDto>>('/announcements/', { params });
  return data;
}

/** `POST /api/announcements/` */
export async function createAnnouncement(input: AnnouncementWriteInput): Promise<AnnouncementDto> {
  const { data } = await apiClient.post<AnnouncementDto>('/announcements/', input);
  return data;
}

/** `PATCH /api/announcements/{public_id}/` */
export async function updateAnnouncement(
  publicId: string,
  input: Partial<AnnouncementWriteInput>
): Promise<AnnouncementDto> {
  const { data } = await apiClient.patch<AnnouncementDto>(`/announcements/${publicId}/`, input);
  return data;
}

/** `DELETE /api/announcements/{public_id}/` — archives server-side (C4). */
export async function deleteAnnouncement(publicId: string): Promise<void> {
  await apiClient.delete(`/announcements/${publicId}/`);
}

/** `POST /api/announcements/{public_id}/{pin|unpin}/` */
export async function setAnnouncementPinned(
  publicId: string,
  pinned: boolean
): Promise<AnnouncementDto> {
  const { data } = await apiClient.post<AnnouncementDto>(
    `/announcements/${publicId}/${pinned ? 'pin' : 'unpin'}/`
  );
  return data;
}

// ---------------------------------------------------------------------------
// Message reports
// ---------------------------------------------------------------------------

/** `POST /api/message-reports/` — open to any participant of that thread. */
export async function createMessageReport(
  input: MessageReportCreateInput
): Promise<MessageReportDto> {
  const { data } = await apiClient.post<MessageReportDto>('/message-reports/', input);
  return data;
}

/** `GET /api/message-reports/` — **admin only** (a report queue is a map of
 *  who said what to whom). */
export async function getMessageReports(
  params: MessageReportListParams = {}
): Promise<Paginated<MessageReportDto>> {
  const { data } = await apiClient.get<Paginated<MessageReportDto>>('/message-reports/', {
    params,
  });
  return data;
}

/** `POST /api/message-reports/{public_id}/review/` — admin only. */
export async function reviewMessageReport(
  publicId: string,
  input: MessageReportReviewInput
): Promise<MessageReportDto> {
  const { data } = await apiClient.post<MessageReportDto>(
    `/message-reports/${publicId}/review/`,
    input
  );
  return data;
}
